/**
 * Portfolio stress test — apply a historical (or custom) market shock
 * to the household's current portfolio and report the projected delta.
 * Phase 11 (2026-05-09).
 *
 * Distinct from `web/src/lib/util/stress-test.ts` (Phase 6 trinity /
 * sequence-of-returns stress-tester for the FIRE engine). This one
 * answers: "what would a 2008-style 37% equity drawdown do to my
 * current $X portfolio today?"
 *
 * Pure math — no Node-only deps. Web mirrors locally per the 5.1 lock.
 *
 * **Pre-baked scenarios** (single-year shock magnitudes, sourced from
 * historical-returns dataset + market-data archives, accessed
 * 2026-05-09):
 *
 *   - **gfc_2008**: Equity -37% (S&P 500 nominal 2008); Bonds +5%
 *     (10Y Treasury 2008 total). Bonds floated up as a flight-to-
 *     quality during the GFC.
 *   - **covid_2020**: Equity -34% (peak-to-trough Feb-Mar 2020);
 *     Bonds +7% (10Y Treasury Q1 2020). Treats v1 as a snapshot of the
 *     drawdown — Hearth doesn't model the V-shaped recovery.
 *   - **inflation_2022**: Equity -19% (S&P 500 calendar 2022);
 *     Bonds -13% (Bloomberg US Agg 2022). Both fell together — the
 *     classic 60/40 failure year.
 *   - **stagflation_70s**: Equity -10% real composite (Hearth 1973-1974
 *     average); Bonds -5% real (real bond return through high inflation).
 *
 * **Sources** (cited verbatim, accessed 2026-05-09):
 *   - S&P 500 calendar-year returns: NYU Stern Damodaran historical-returns
 *     https://pages.stern.nyu.edu/~adamodar/New_Home_Page/datafile/histretSP.html
 *   - 10Y Treasury / Bloomberg US Agg returns: Federal Reserve FRED + AGG
 *     ETF total-return reports
 *
 * **Limitations**:
 *   - Single-year application. The actual 2008-09 cycle had a partial
 *     recovery in 2009; v1 reports the worst-year shock only.
 *   - Cash, real estate, alternatives are bucketed under the "other"
 *     classification and assumed to follow bonds.
 *   - International equity is treated as US equity (tighter v2 model
 *     would split developed-ex-US and EM).
 */

export type StressScenarioKey =
  | "gfc_2008"
  | "covid_2020"
  | "inflation_2022"
  | "stagflation_70s"
  | "custom";

export interface StressScenarioDef {
  readonly key: StressScenarioKey;
  readonly label: string;
  readonly equity_delta_pct: number;
  readonly bond_delta_pct: number;
  /** Other-asset (cash, real estate, alts) delta — defaults to bond_delta. */
  readonly other_delta_pct?: number;
}

export const STRESS_SCENARIOS: ReadonlyArray<StressScenarioDef> = [
  {
    key: "gfc_2008",
    label: "Global Financial Crisis (2008)",
    equity_delta_pct: -37,
    bond_delta_pct: 5,
  },
  {
    key: "covid_2020",
    label: "COVID-19 flash drawdown (Feb–Mar 2020)",
    equity_delta_pct: -34,
    bond_delta_pct: 7,
  },
  {
    key: "inflation_2022",
    label: "Inflation + rates 2022",
    equity_delta_pct: -19,
    bond_delta_pct: -13,
  },
  {
    key: "stagflation_70s",
    label: "Stagflation (1973–74 composite, real)",
    equity_delta_pct: -10,
    bond_delta_pct: -5,
  },
];

export const STRESS_SCENARIOS_BY_KEY: ReadonlyMap<StressScenarioKey, StressScenarioDef> =
  new Map(STRESS_SCENARIOS.map((s) => [s.key, s]));

export type AssetClass = "equity" | "bond" | "other";

export interface StressInputBucket {
  readonly asset_class: AssetClass;
  readonly value_cents: number;
  /** Optional label (e.g., account_type or sleeve) for breakdown rendering. */
  readonly label?: string;
}

export interface StressResultBucket {
  readonly asset_class: AssetClass;
  readonly label?: string;
  readonly before_cents: number;
  readonly after_cents: number;
  readonly delta_cents: number;
}

export interface StressResult {
  readonly scenario: StressScenarioDef;
  readonly before_cents: number;
  readonly after_cents: number;
  readonly delta_cents: number;
  readonly delta_pct: number;
  readonly buckets: readonly StressResultBucket[];
}

export function applyStressScenario(args: {
  readonly buckets: readonly StressInputBucket[];
  readonly scenario: StressScenarioDef;
}): StressResult {
  const { scenario } = args;
  const otherDelta = scenario.other_delta_pct ?? scenario.bond_delta_pct;
  const buckets: StressResultBucket[] = args.buckets.map((b) => {
    const delta =
      b.asset_class === "equity"
        ? scenario.equity_delta_pct
        : b.asset_class === "bond"
          ? scenario.bond_delta_pct
          : otherDelta;
    const after = Math.round(b.value_cents * (1 + delta / 100));
    const result: StressResultBucket = {
      asset_class: b.asset_class,
      before_cents: b.value_cents,
      after_cents: after,
      delta_cents: after - b.value_cents,
    };
    if (b.label !== undefined) {
      return { ...result, label: b.label };
    }
    return result;
  });
  const before = buckets.reduce((s, b) => s + b.before_cents, 0);
  const after = buckets.reduce((s, b) => s + b.after_cents, 0);
  return {
    scenario,
    before_cents: before,
    after_cents: after,
    delta_cents: after - before,
    delta_pct: before > 0 ? ((after - before) / before) * 100 : 0,
    buckets,
  };
}

/**
 * Classifies a ticker into asset_class for stress-test bucketing.
 * v1 heuristics:
 *   - Tickers with "BND" / "BNDX" / "AGG" / "TIP" / "VTIP" / "GOVT" /
 *     "SHY" / "FXNAX" / "SCHZ" / "VBTLX" → "bond"
 *   - Money-market tickers → "other" (not equity, not classic-duration bond)
 *   - All else → "equity"
 *
 * Phase 12+ may consult the expense-ratios table's `fund_type` for a
 * cleaner classification.
 */
export function classifyAssetClass(ticker: string): AssetClass {
  if (!ticker) return "other";
  const u = ticker.toUpperCase();
  const bondTickers = new Set([
    "BND",
    "BNDX",
    "AGG",
    "TIP",
    "VTIP",
    "GOVT",
    "SHY",
    "FXNAX",
    "SCHZ",
    "VBTLX",
  ]);
  if (bondTickers.has(u)) return "bond";
  const mmTickers = new Set(["VMFXX", "SPAXX", "SWVXX"]);
  if (mmTickers.has(u)) return "other";
  return "equity";
}
